import type { Schema } from "@loanlink-nl/zero";
import type { Row } from "@rocicorp/zero";
import type { MaybeRefOrGetter } from "vue";
import { type ComputedRef, computed, toValue } from "vue";

type ValidationMessage = Row<Schema["tables"]["dataRequestValidationTable"]>;
export type DataRequest = Row<Schema["tables"]["dataRequestTable"]>;

type DataRequestOverallStatus = DataRequest["status"];

type DataRequestStatus = "pending" | "success" | "failed";
type DatakeeperStatus =
	| "CREATED"
	| "PENDING"
	| "CONFIRMED"
	| "CONFIRMED_UNSUCCESSFUL_ZKP"
	| "CANCELED"
	| "FAILED"
	| "EXPIRED";

export interface SourceStatus {
	sourceType: string;
	sourceLabel: string;
	status: DataRequestStatus;
	validationMessages: ValidationMessage[];
}

export interface DataRequestStatusWithValidation {
	overallStatus: DataRequestOverallStatus;
	sourceStatuses: SourceStatus[];
	hasData: boolean;
}

const supportedSources = [
	"LoondienstInkomstenBericht",
	"PensioenOverzichtBericht",
	"BasisRegistratiePersonenBericht",
	"DigitaleIdentificatieBericht",
] as const;

export const supportedSourcesLabels: Record<
	(typeof supportedSources)[number] | "unknown",
	string
> = {
	LoondienstInkomstenBericht: "UWV Verzekeringsbericht",
	PensioenOverzichtBericht: "Mijnpensioenoverzicht.nl",
	BasisRegistratiePersonenBericht: "MijnOverheid",
	DigitaleIdentificatieBericht: "Identificatie",
	unknown: "Onbekende bron",
};

export const supportedDatakeeperSources = {
	"id-card": "ID-kaart",
	passport: "Paspoort",
	myNlGovernment: "MijnOverheid",
	"pension-document": "Mijnpensioenoverzicht.nl",
	"ibl-income": "Gecertificeerd inkomen (IBL)",
	"pre-filled-tax-info": "Inkomen, bezittingen en schulden (VIA)",
	duo: "Studieschuld (DUO)",
	unknown: "Onbekende bron",
};

const datakeeperStatusToStatus: Record<DatakeeperStatus, DataRequestStatus> = {
	CREATED: "pending",
	PENDING: "pending",
	CONFIRMED: "success",
	CONFIRMED_UNSUCCESSFUL_ZKP: "success",
	CANCELED: "failed",
	FAILED: "failed",
	EXPIRED: "failed",
};

export function parseDatakeeperPredicates(
	content: any,
): Record<string, { status: DatakeeperStatus; value: unknown }> | undefined {
	if (!content) {
		return;
	}

	const items = content
		.map((x: any) => ({
			value: x.details.predicateValues,
			context: x.context[0],
			status: x.status,
		}))
		.toSorted((a: any, b: any) => a.context - b.context);

	return items.reduce((red: any, x: any) => {
		const context = x.context.split("#")[1];
		red[context] ??= {};
		red[context].status = x.status;
		red[context].value ??= {};
		Object.assign(red[context].value, x.value);
		return red;
	}, {});
}

function pendingStatus(): DataRequestStatusWithValidation {
	return {
		overallStatus: "pending",
		sourceStatuses: [],
		hasData: false,
	};
}
export function useDataRequestStatus(
	dataRequest: MaybeRefOrGetter<DataRequest | undefined>,
	validationMessages: MaybeRefOrGetter<
		Readonly<ValidationMessage[]> | undefined
	>,
): ComputedRef<DataRequestStatusWithValidation> {
	return computed(() => {
		const channel = toValue(dataRequest)?.channel;
		if (channel === "datakeeper") {
			return useDataRequestDatakeeperStatus(dataRequest).value;
		}
		if (channel === "hdn") {
			return useDataRequestHdnStatus(dataRequest, validationMessages).value;
		}
		return pendingStatus();
	});
}

function useDataRequestDatakeeperStatus(
	dataRequest: MaybeRefOrGetter<DataRequest | undefined>,
): ComputedRef<DataRequestStatusWithValidation> {
	return computed(() => {
		const request = toValue(dataRequest);
		if (!request) {
			return pendingStatus();
		}

		if (request.channel !== "datakeeper") {
			return pendingStatus();
		}

		const content = parseDatakeeperPredicates(request.content);

		return {
			overallStatus: request.status,
			sourceStatuses: content
				? Object.entries(content).map(([predicate, content]) => {
						return {
							sourceType: predicate,
							sourceLabel:
								supportedDatakeeperSources[
									predicate as keyof typeof supportedDatakeeperSources
								] ?? supportedDatakeeperSources.unknown,
							status: datakeeperStatusToStatus[content.status],
							validationMessages: [],
						};
					})
				: [],
			hasData: Boolean(request.content),
		};
	});
}

function useDataRequestHdnStatus(
	dataRequest: MaybeRefOrGetter<DataRequest | undefined>,
	validationMessages: MaybeRefOrGetter<
		Readonly<ValidationMessage[]> | undefined
	>,
): ComputedRef<DataRequestStatusWithValidation> {
	return computed(() => {
		const request = toValue(dataRequest);
		const messages = toValue(validationMessages);

		if (!request) {
			return pendingStatus();
		}

		if (request.channel !== "hdn") {
			return pendingStatus();
		}

		const hasData = Boolean(
			request.content && Object.keys(request.content).length > 0,
		);

		if (!request.status || request.status === "pending") {
			return pendingStatus();
		}

		const sourceGroups = groupMessagesBySource(messages ?? []);
		const sourceStatuses = calculateSourceStatuses(
			sourceGroups,
			request.content,
		);

		const hasFailedSources = sourceStatuses.some((s) => s.status === "failed");
		const hasSuccessfulSources =
			hasData || sourceStatuses.some((s) => s.status === "success");

		if (hasSuccessfulSources && hasFailedSources) {
			return {
				overallStatus: "failed",
				sourceStatuses,
				hasData,
			};
		}

		if (hasSuccessfulSources) {
			return {
				overallStatus: "completed",
				sourceStatuses,
				hasData,
			};
		}

		return {
			overallStatus: "opened",
			sourceStatuses,
			hasData,
		};
	});
}

function groupMessagesBySource(
	messages: Readonly<ValidationMessage[]>,
): Record<string, ValidationMessage[]> {
	const sourceGroups = messages.reduce(
		(groups, message) => {
			const sourceType = message.sourceType || "unknown";
			if (!groups[sourceType]) {
				groups[sourceType] = [];
			}
			groups[sourceType].push(message);
			return groups;
		},
		supportedSources.reduce(
			(groups, source) => {
				groups[source] = [];
				return groups;
			},
			{} as Record<string, ValidationMessage[]>,
		),
	);

	return sourceGroups;
}

function calculateSourceStatuses(
	sourceGroups: Record<string, ValidationMessage[]>,
	data: any,
): SourceStatus[] {
	const sourceStatuses: SourceStatus[] = [];

	for (const [sourceType, messages] of Object.entries(sourceGroups)) {
		let status: "success" | "failed" | "pending" = "pending";

		if (messages.length > 0) {
			status = "failed";
		} else if (data?.[sourceType]) {
			status = "success";
		}

		sourceStatuses.push({
			sourceType,
			sourceLabel:
				supportedSourcesLabels[
					sourceType as keyof typeof supportedSourcesLabels
				],
			status,
			validationMessages: messages,
		});
	}

	return sourceStatuses;
}
